import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import ErrorLogDetailsModal from 'components/admin/error-log-details-modal';
import ErrorLogFilterField from 'components/admin/error-log/error-log-filter-field';
import ErrorLogSortHeader from 'components/admin/error-log/error-log-sort-header';
import Button from 'components/inputs/button';
import Select from 'components/inputs/select';
import Text from 'components/inputs/text';
import { useGetErrorLogsQuery } from 'features/api/error-log-api';
import {
  buildErrorTypeOptions,
  buildPageOptions,
  buildUserOptions,
  defaultErrorLogFilters,
  errorLogMatchesFilters,
  getLoggedColumnAriaSort,
  hasActiveErrorLogFilters,
  sortErrorLogsByLogged
} from 'pages/admin/error-logs-helpers';
import { useMemo, useState } from 'react';
import {
  ErrorLogColumnType,
  ErrorLogFiltersType,
  ErrorLogListItemType,
  ErrorLogSortDirectionType
} from 'types/error-log';
import {
  formatErrorLogType,
  formatErrorLogUser,
  formatLoggedAt
} from 'utils/error-log/format';
import { buildReproduceUrl, canReproduce } from 'utils/error-log/reproduce';
import { truncateString } from 'utils/error-log/serialize';

const COLUMNS: ErrorLogColumnType[] = [
  { key: 'loggedAt', label: 'Logged', sortable: true },
  { key: 'user', label: 'User' },
  { key: 'page', label: 'Page' },
  { key: 'type', label: 'Type' },
  { key: 'message', label: 'Message' },
  { key: 'actions', label: '' }
];
const MESSAGE_PREVIEW_LENGTH = 140;

export default function ErrorLogsPage() {
  const {
    data: logs = [],
    isLoading,
    isFetching,
    isError,
    refetch
  } = useGetErrorLogsQuery();
  const [filters, setFilters] = useState<ErrorLogFiltersType>(
    defaultErrorLogFilters
  );
  const [selectedLogId, setSelectedLogId] = useState<number | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [loggedSortDirection, setLoggedSortDirection] =
    useState<ErrorLogSortDirectionType>('desc');

  const userOptions = useMemo(() => buildUserOptions(logs), [logs]);
  const errorTypeOptions = useMemo(() => buildErrorTypeOptions(logs), [logs]);
  const pageOptions = useMemo(() => buildPageOptions(logs), [logs]);
  const filteredLogs = useMemo(
    () =>
      sortErrorLogsByLogged(
        logs.filter((log) => errorLogMatchesFilters(log, filters)),
        loggedSortDirection
      ),
    [logs, filters, loggedSortDirection]
  );

  const updateFilter = (key: keyof ErrorLogFiltersType, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const openDetails = (log: ErrorLogListItemType) => {
    setSelectedLogId(log.id);
    setDetailsOpen(true);
  };

  const reproduce = (log: ErrorLogListItemType) => {
    window.open(buildReproduceUrl(log), '_blank', 'noopener');
  };

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-3xl font-bold text-gray-900'>Error Logs</div>
        <Button
          text='Refresh'
          color='white'
          icon={ArrowPathIcon}
          loading={isFetching}
          onClick={() => refetch()}
        />
      </div>
      <div className='text-sm text-gray-600'>
        Errors captured automatically from the System Selector (error modals,
        crashes, unhandled errors) plus manual reports sent from the debug menu.
        Entries older than two weeks are purged automatically.
      </div>

      <div className='flex flex-wrap items-end gap-3'>
        <ErrorLogFilterField label='User'>
          <Select
            value={filters.userId}
            onChange={(value) => updateFilter('userId', String(value))}
            options={userOptions}
            placeholder='All users'
          />
        </ErrorLogFilterField>
        <ErrorLogFilterField label='Error type'>
          <Select
            value={filters.errorType}
            onChange={(value) => updateFilter('errorType', String(value))}
            options={errorTypeOptions}
            placeholder='All types'
          />
        </ErrorLogFilterField>
        <ErrorLogFilterField label='Page'>
          <Select
            value={filters.page}
            onChange={(value) => updateFilter('page', String(value))}
            options={pageOptions}
            placeholder='All pages'
          />
        </ErrorLogFilterField>
        <ErrorLogFilterField label='Search' className='w-72'>
          <Text
            value={filters.search}
            onChange={(value) => updateFilter('search', value)}
            placeholder='Message, URL, user...'
            controlled
          />
        </ErrorLogFilterField>
        <Button
          text='Clear filters'
          color='subtle'
          onClick={() => setFilters(defaultErrorLogFilters)}
          disabled={!hasActiveErrorLogFilters(filters)}
        />
      </div>

      {isError && (
        <div className='rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700'>
          Failed to load the error logs.
        </div>
      )}

      <div className='max-h-[65vh] overflow-auto'>
        <table className='min-w-full border-separate border-spacing-0 divide-y divide-gray-300'>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column.key}
                  scope='col'
                  aria-sort={getLoggedColumnAriaSort(
                    column.sortable,
                    loggedSortDirection
                  )}
                  className='sticky top-0 z-10 border-b border-gray-300 bg-gray-100 px-3 pb-3.5 pt-3.5 text-left text-sm font-semibold text-gray-900'
                >
                  {column.sortable ? (
                    <ErrorLogSortHeader
                      label={column.label}
                      direction={loggedSortDirection}
                      onToggle={() =>
                        setLoggedSortDirection((prev) =>
                          prev === 'desc' ? 'asc' : 'desc'
                        )
                      }
                    />
                  ) : (
                    column.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {filteredLogs.map((log) => (
              <tr
                key={log.id}
                className='cursor-pointer hover:bg-gray-50'
                onClick={() => openDetails(log)}
              >
                <td className='whitespace-nowrap px-3 py-3 text-sm text-gray-500'>
                  {formatLoggedAt(log.createdAt)}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-900'>
                  <div>{formatErrorLogUser(log)}</div>
                  {log.userEmail && (
                    <div className='text-xs font-normal text-gray-500'>
                      {log.userEmail}
                    </div>
                  )}
                </td>
                <td className='whitespace-nowrap px-3 py-3 font-mono text-xs text-gray-600'>
                  {log.page}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm text-gray-500'>
                  {formatErrorLogType(log.errorType)}
                </td>
                <td className='px-3 py-3 text-sm text-gray-700'>
                  <div className='max-w-[32rem]' title={log.errorMessage}>
                    {truncateString(log.errorMessage, MESSAGE_PREVIEW_LENGTH)}
                  </div>
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-right'>
                  <div className='flex justify-end gap-2'>
                    <Button
                      text='Details'
                      color='white'
                      icon={EyeIcon}
                      iconSize='sm'
                      onClick={(event) => {
                        event?.stopPropagation();
                        openDetails(log);
                      }}
                    />
                    <Button
                      text='Reproduce'
                      icon={ArrowTopRightOnSquareIcon}
                      iconSize='sm'
                      disabled={!canReproduce(log)}
                      onClick={(event) => {
                        event?.stopPropagation();
                        reproduce(log);
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
            {!isLoading && filteredLogs.length === 0 && (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className='py-8 text-center text-sm text-gray-400'
                >
                  {logs.length === 0
                    ? 'No errors have been logged in the last two weeks.'
                    : 'No error logs match the current filters.'}
                </td>
              </tr>
            )}
            {isLoading && (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className='py-8 text-center text-sm text-gray-400'
                >
                  Loading error logs...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className='text-sm text-gray-400'>
        {filteredLogs.length} of {logs.length} error log
        {logs.length !== 1 ? 's' : ''}
      </div>

      <ErrorLogDetailsModal
        openState={[detailsOpen, setDetailsOpen]}
        errorLogId={selectedLogId}
      />
    </div>
  );
}
